import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { GroupPhoto } from '../../components/GroupPhoto.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import type { Friend, Group, Person } from '../../domain.ts';

/**
 * F1 — 친구 · 모임 리스트.
 *
 * 기획서 2.5 대로 모임과 친구가 한 화면에 있지만 성격이 달라 섹션을 나눴습니다.
 * 친구 방은 공유가 일어날 때 자동 생성되므로 사용자가 만들 수 없습니다 —
 * 그래서 모임과 같은 줄에 두지 않고 아래 섹션으로 내렸습니다.
 *
 * **검색창은 두 섹션을 함께 훑습니다.** 섹션마다 창을 두면 무엇을 찾을지부터
 * 정해야 합니다 — 치는 대로 모임과 친구가 함께 좁혀지는 편이 빠릅니다.
 * 섹션 옆의 숫자도 좁혀진 뒤의 것입니다.
 *
 * **만드는 입구는 우하단 FAB 하나입니다.** 우상단 ＋ 와 목록 사이의
 * 「모임 만들기」 버튼을 없앴습니다 — 같은 일을 하는 버튼이 한 화면에 둘이면
 * 어느 쪽이 진짜인지 알 수 없고, 목록 사이에 낀 버튼은 스크롤과 함께 사라집니다.
 *
 * FAB 은 **바로 모임 생성(F2)** 으로 갑니다. C1 처럼 한 칸을 두지 않은 것은
 * 여기서 만들 수 있는 것이 모임 하나뿐이라, 고를 것이 없기 때문입니다.
 *
 * 아래 섹션은 **대표 기도제목을 공유해 주는 친구**입니다. 공유가 일어날 때
 * 자동으로 생기는 «친구 방»의 자리인데, 방을 따로 만들지 않고 서로 친구인
 * 사람을 그대로 보여 줍니다 — 친구가 되면 대표 기도제목이 보이는 것이
 * 기획서의 관계 규칙이고, 그것이 곧 «공유받은 것»입니다.
 */

interface Props {
  groups: Group[] | null;
  friends: Friend[] | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  onOpenGroup: (groupId: string) => void;
  onOpenFriend: (person: Person) => void;
  onNewGroup: () => void;
}

export function GroupList({
  groups,
  friends,
  loading,
  error,
  onReload,
  onOpenGroup,
  onOpenFriend,
  onNewGroup,
}: Props) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const hit = (name: string | null) => (name ?? '').toLowerCase().includes(needle);

  const foundGroups = (groups ?? []).filter((g) => hit(g.name));
  // 서로 친구인 사람만 — 차단한 쪽은 대표 기도제목이 보이지 않으므로 공유도 없습니다
  const foundFriends = (friends ?? []).filter((f) => f.mutual && hit(f.name));
  const nothing = foundGroups.length === 0 && foundFriends.length === 0;

  return (
    <div className="screen">
      {/* 우상단 ＋ 자리를 비웠습니다. 만드는 입구는 아래 FAB 하나입니다 */}
      <NavBar title="Prayer Room" large />

      <div className="screen__body">
        <SearchBox value={query} onChange={setQuery} placeholder="모임 · 친구 이름 검색" />

        {loading && <Loading label="모임을 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={onReload} />}

        {!loading && nothing && (
          <div className="empty">
            <p className="empty__title">
              {needle === '' ? '아직 모임이 없습니다' : '찾는 모임이나 친구가 없습니다'}
            </p>
            <p className="empty__hint">
              {needle === ''
                ? '오른쪽 아래 ＋ 로 첫 모임을 만들어 보세요.'
                : '이름으로 찾습니다. 다른 말로 찾아보세요.'}
            </p>
          </div>
        )}

        {foundGroups.length > 0 && (
          <>
            <p className="section-title">
              모임 <span>{foundGroups.length}</span>
            </p>
            <div className="list">
              {foundGroups.map((g) => (
                <button
                  key={g.groupId}
                  type="button"
                  className="row"
                  onClick={() => onOpenGroup(g.groupId)}
                >
                  {/* 사진을 고르지 않았으면 이름 앞 두 글자 */}
                  {g.photo === undefined ? (
                    <Avatar name={g.name} seed={g.groupId} square />
                  ) : (
                    <GroupPhoto photo={g.photo} avatar />
                  )}
                  <span className="row__grow">
                    <span className="row__name">{g.name}</span>
                    <span className="row__sub">{g.latest ?? (g.intro || '아직 기록지가 없습니다')}</span>
                  </span>
                  <span className="pill pill--status" data-status="IN_PROGRESS">
                    {g.members.length}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {foundFriends.length > 0 && (
          <>
            <p className="section-title">
              공유받은 기도 노트 <span>{foundFriends.length}</span>
            </p>
            <div className="list">
              {foundFriends.map((f) => (
                <button
                  key={f.userId}
                  type="button"
                  className="row"
                  onClick={() => onOpenFriend(f)}
                >
                  <Avatar name={f.name ?? '?'} seed={f.userId} />
                  <span className="row__grow">
                    <span className="row__name">{f.name ?? '이름 없음'}</span>
                    <span className="row__sub">대표 기도제목 보기</span>
                  </span>
                  <Icon name="forward" size={16} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <button type="button" className="fab" aria-label="모임 만들기" onClick={onNewGroup}>
        <Icon name="plus" size={23} />
      </button>
    </div>
  );
}
